/**
 * Shared Rovo animated gradient border.
 *
 * The conic gradient rotates via a CSS custom property (`--gradient-angle`)
 * animated with `@keyframes`. The property is registered via
 * `CSS.registerProperty()` so the browser can interpolate it as an angle.
 *
 * IMPORTANT: The gradient must be applied directly on the animated element
 * (not via a CSS variable on `:root`) because the keyframe animation changes
 * `--gradient-angle` on the animated element, not on `:root`.
 *
 * Usage:
 *   1. Render `<RovoGradientKeyframesStyle />` somewhere in your component tree
 *   2. Apply to your element:
 *      ```ts
 *      style={{
 *        background: ROVO_GRADIENT_BACKGROUND,
 *        animation: 'rovoGradientRotation linear 2s infinite',
 *      }}
 *      ```
 */

import React, { useEffect } from 'react';

// ── Gradient background value ───────────────────────────────────────────────

/**
 * Conic gradient string to apply directly as the `background` on the animated
 * element. Uses `var(--gradient-angle)` which is interpolated by the
 * `rovoGradientRotation` keyframes.
 */
export const ROVO_GRADIENT_BACKGROUND = `conic-gradient(
	from var(--gradient-angle, 45deg),
	#FCA700 0%,
	#6A9A23 25%,
	#1868DB 50%,
	#AF59E0 75%,
	#FCA700 100%
)`;

// ── Register @property imperatively ─────────────────────────────────────────

let propertyRegistered = false;

function ensureGradientAngleRegistered(): void {
	if (propertyRegistered) {
		return;
	}

	if (typeof window === 'undefined' || typeof CSS === 'undefined') {
		return;
	}

	propertyRegistered = true;

	try {
		if (typeof CSS.registerProperty === 'function') {
			CSS.registerProperty({
				name: '--gradient-angle',
				syntax: '<angle>',
				initialValue: '270deg',
				inherits: false,
			});
		}
	} catch {
		// Property may already be registered, or browser doesn't support it.
		// The gradient will still render but without rotation animation.
	}
}

// ── Component ───────────────────────────────────────────────────────────────

const KEYFRAMES_CSS = `
@keyframes rovoGradientRotation {
	0% { --gradient-angle: 0deg; }
	100% { --gradient-angle: 360deg; }
}
`;

/**
 * Injects `@keyframes rovoGradientRotation` and registers `--gradient-angle`
 * via `CSS.registerProperty()` so the browser can animate it.
 *
 * Render this component once in your tree alongside the animated element.
 */
export const RovoGradientKeyframesStyle = (): React.ReactElement => {
	useEffect(() => {
		ensureGradientAngleRegistered();
	}, []);

	// eslint-disable-next-line react/no-danger, @atlaskit/ui-styling-standard/no-global-styles -- @keyframes for CSS custom property animation
	return React.createElement('style', {
		dangerouslySetInnerHTML: { __html: KEYFRAMES_CSS },
	});
};
